import { axiosInstance } from '../utils/axiosApp';
import type { StoredOrg } from '../groups/GroupsApi';
import { AccessLevel, ReadingStage } from '../utils/constants';
import type { StoredProfile } from '../store/slices/profileAccessSlice';

export type OwnerAccount = {
  name: string | null;
};

export type ProfileDataResponse = {
  profile_id: string;
  name: string;
  avatar: string;
  level: AccessLevel;
  is_owner: boolean;
  age: number;
  reading: ReadingStage;
  language?: string | null;
  other_langs?: string[];
  owner?: OwnerAccount | null;
  org?: StoredOrg | null;
};

export type ProfileGroupSummary = {
  illustration?: string | null;
  illustration_url?: string | null;
  group_id: string;
  name: string;
  org?: StoredOrg | null;
  grade?: string | null;
  country?: string | null;
  age_min?: number | null;
  age_max?: number | null;
};

export const fetchProfiles = () => axiosInstance().get('/api/profiles');

export const fetchOwnedProfiles = () =>
  axiosInstance().get<StoredProfile[]>('/api/profiles/owned', {
    timeout: 15_000,
  });

export const fetchProfileData = (profileId: string, signal?: AbortSignal) => {
  return axiosInstance().get<ProfileDataResponse>(
    `/api/profiles/${profileId}`,
    {
      signal,
      timeout: 15_000,
    }
  );
};

export const fetchProfileGroups = (profileId: string, signal?: AbortSignal) => {
  return axiosInstance().get<ProfileGroupSummary[]>(
    `/api/profiles/${encodeURIComponent(profileId)}/groups`,
    { signal, timeout: 15_000 }
  );
};

export const leaveProfileGroup = async (
  profileId: string,
  groupId: string
): Promise<void> => {
  await axiosInstance().post(
    `/api/profiles/${encodeURIComponent(profileId)}/groups/${encodeURIComponent(groupId)}/leave`
  );
};

export const createProfile = (data: {
  name: string;
  age?: number | null;
  birth_year: number;
  language: string;
  other_langs?: string[];
  avatar: string;
  reading: ReadingStage;
}) => axiosInstance().post('/api/profiles', data);

export const updateCurrentProfile = (
  profileId: string,
  data: {
    name?: string;
    language?: string;
    other_langs?: string[];
    birth_year: number;
    avatar?: string;
    reading: ReadingStage;
  }
) =>
  axiosInstance().put(`/api/profiles/${profileId}`, data, { timeout: 30_000 });
